import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { ENABLE_OFFLINE_ASSESSMENT } from '../../http/api-client.tokens';

/**
 * Central Offline Bypass Interceptor for Conductor
 *
 * When the browser is offline (!navigator.onLine) and offline assessment is enabled, intercepts outgoing HTTP requests
 * targeting backend API endpoints and returns a safe empty HttpResponse immediately.
 *
 * When offline assessment is disabled, requests pass through normally.
 */
export const offlineBypassInterceptor: HttpInterceptorFn = (req, next) => {
  const isOfflineAssessmentEnabled = inject(ENABLE_OFFLINE_ASSESSMENT, { optional: true }) ?? false;

  if (isOfflineAssessmentEnabled && typeof navigator !== 'undefined' && !navigator.onLine) {
    // Write requests (POST, PUT, PATCH, DELETE) MUST NOT be intercepted with synthetic 200 responses.
    // They must either proceed to offline queuing or fail as network errors.
    if (req.method !== 'GET') {
      return next(req);
    }

    // Inspect request query params for paging
    const isPaged =
      req.params.has('pageIndex') ||
      req.params.has('pageSize') ||
      req.params.has('skip') ||
      req.params.has('take') ||
      req.params.has('page');

    if (isPaged) {
      return of(
        new HttpResponse({
          status: 200,
          body: { items: [], totalItems: 0, totalPages: 0, pageIndex: 1, pageSize: 10 },
        })
      );
    }

    const urlLower = (req.url || '').toLowerCase();

    // Check if GET URL targets a single resource by ID (number, negative integer, or GUID)
    // or known single-resource endpoints
    const isSingleResource =
      /\/(?:-?[0-9]+|[a-f0-9-]{36})\/?$/i.test(urlLower) ||
      urlLower.includes('/profile') ||
      urlLower.includes('/verification') ||
      urlLower.includes('/clinical-summary');

    if (isSingleResource) {
      return of(new HttpResponse({ status: 200, body: null }));
    }

    // Default for collection GET endpoints is an empty array
    return of(new HttpResponse({ status: 200, body: [] }));
  }
  return next(req);
};
